/* eslint-disable no-undef */
import {
	debounce,
	round,
	throttle
} from "lodash";
import template from "./modal.html";
import {
	every,
	values
} from "lodash";
import {
	FIELD_KEYS,
	EVENT_LOGOUT
} from "constants.js";

import { CustomPopup } from "./CustomPopup";
import { UNITS } from "../../../constants";
import { idsMatch } from "../../../utils/valueUtilities";
import reefIcon from "../../../images/reef.svg";
import { toFeatures } from "../../../utils/geometries-to-geojson";

const settings = {
	pixelOffset: { width: 0, height: -45 },
	selectedZoom: 5
};

const DEFAULT_RANGE_NORTH = 51;
const DEFAULT_RANGE_EAST = -63;
const DEFAULT_RANGE_SOUTH = 24;
const DEFAULT_RANGE_WEST = -128;

const defaultUSABounds = {
	north: DEFAULT_RANGE_NORTH,
	south: DEFAULT_RANGE_SOUTH,
	east: DEFAULT_RANGE_EAST,
	west: DEFAULT_RANGE_WEST
};

export class Controller {
	lookup = null;
	mapInstance = null;
	infoWindow = new window.google.maps.InfoWindow(settings);
	marker = new google.maps.Marker();
	relatedFieldSelections = [];
	selectedFeatures = {};
	initialRelatedValueByFieldId = {};
	locationFieldName = "Location";

	selected = {
		marker: new google.maps.Marker(),
		infoWindow: new window.google.maps.InfoWindow(settings),
		position: null
	};
	hasFeatureLayer = false;

	constructor($scope, mapHelpers, $base64, eventBus, $logger) {
		this._scope = $scope;
		this._mapHelpers = mapHelpers;
		this._base64 = $base64;
		this.eventBus = eventBus;
		this.$logger = $logger;
	}

	$onInit() {
		this.lookup = this.resolve.lookup;
		this.locationFieldName = this.resolve.locationFieldName ?? "Location";
		this.updatable = this.resolve.lookup.isUpdatable();
		this.debouncedLoadElevation = debounce(this.loadElevation, 60);
		this.elevation = undefined;
		const savedRelatedValues = (this.resolve.selectedRelatedValues ?? []).reduce((valuesByField, value) => {
			valuesByField[value.fieldId] = value;
			return valuesByField;
		}, {});
		this.initialRelatedValueByFieldId = savedRelatedValues;
		this.relatedFieldSelections = (this.lookup?.relatedFields ?? []).reduce((fields, field) => {
			fields.push({
				Id: field.Id,
				Name: field.Name,
				value: savedRelatedValues[field.Id] ?? null
			});

			return fields;
		}, []);

		this.initializeMap();
		this.setMapLayer();
		this.hydrateSelectedFeatures();

		const locationVals = values(this.resolve.location);
		const hasLocation = locationVals.length > 0 && every(locationVals);

		if(hasLocation && typeof locationVals[0] === "number" && typeof locationVals[1] === "number") {
			try {
				const latLng = new window.google.maps.LatLng({ lat: locationVals[0], lng: locationVals[1] });
				this.selected.position = latLng.lat() && latLng.lng() ? {
					lat: latLng.lat(),
					lng: latLng.lng()
				} : undefined;
			}catch(err) {
				this.$logger.warn(`Google likely could not construct a valid latLng object given the input of ${locationVals[0]}, ${locationVals[1]}`);
			}
		}

		if(this.selected.position) {
			this.debouncedLoadElevation(this.selected.position);
			const content = this.calloutHtml(this.selected.position);

			this.showInfoWindow(content, this.selected.position);
			this.setStationaryMarker(this);
		}

		this.eventBus.register(EVENT_LOGOUT, () => { this.done(); }, this._scope);

		// update the info window whenever the elevation value changes.
		this._scope.$watch(this.elevation, (newVal) => {
			newVal && this.updateInfoWindow(this.infoWindow, newVal);
		});
	}

	done() {
		const relatedValues = this.relatedFieldSelections.reduce((values, field) => {
			if(field.value) {
				values.push(field.value);
			}

			return values;
		}, []);

		const clearedRelatedFieldIds = this.relatedFieldSelections.reduce((fieldIds, field) => {
			if(this.initialRelatedValueByFieldId[field.Id] && !field.value) {
				fieldIds.push(field.Id);
			}

			return fieldIds;
		}, []);

		this.modalInstance.close({
			...(this.selected.position ?? {}),
			relatedValues,
			clearedRelatedFieldIds
		});
	}

	clearSelectedField(fieldId) {
		const field = this.relatedFieldSelections.find(selection => selection.Id === fieldId);

		if(field) {
			field.value = null;
		}

		delete this.selectedFeatures[fieldId];
		this.revertStyle();
		this._scope.$evalAsync();
	}

	getSelectedLocationText() {
		if(!this.selected.position) {
			return "--";
		}

		const roundingPoint = this.resolve.lookup.roundingPoint ?? 6;

		if(this.resolve.lookup.mode === "DECIMAL_DEGREES") {
			return `${round(this.selected.position.lat, roundingPoint)}°, ${round(this.selected.position.lng, roundingPoint)}°`;
		}

		const latDMS = this.resolve.lookup.degreeMapper(this.selected.position.lat, true);
		const lngDMS = this.resolve.lookup.degreeMapper(this.selected.position.lng, true);

		return `${this.formatMappedCoordinate(latDMS, "S", "N")}, ${this.formatMappedCoordinate(lngDMS, "W", "E")}`;
	}

	formatMappedCoordinate(value, negativeLetter, positiveLetter, roundDegrees = false) {
		const degrees = roundDegrees ? round(value.degrees) : value.degrees;
		const minutes = value.roundedMinutes ?? value.minutes;
		return `${degrees}°${minutes}'${value.isNegative ? negativeLetter : positiveLetter}`;
	}

	setRelatedFieldSelection(feature) {
		if(!feature || typeof feature.getProperty !== "function") {
			return;
		}

		const fieldId = feature.getProperty("fieldId");
		const id = feature.getProperty("id");
		const name = feature.getProperty("name");

		if(!fieldId || !id) {
			return;
		}

		const relatedField = this.relatedFieldSelections.find(field => field.Id === fieldId);

		if(relatedField) {
			relatedField.value = {
				Id: id,
				Name: name,
				fieldId
			};

			this.selectedFeatures[fieldId] = feature;
			this.revertStyle();
		}
	}

	hydrateSelectedFeatures() {
		if(!this.hasFeatureLayer || typeof this.layer.forEach !== "function") {
			return;
		}

		const selectedIdsByFieldId = this.relatedFieldSelections.reduce((selectedIds, field) => {
			if(field.value && field.value.Id) {
				selectedIds[field.Id] = field.value.Id;
			}

			return selectedIds;
		}, {});

		this.selectedFeatures = {};
		this.layer.forEach((feature) => {
			const fieldId = feature.getProperty("fieldId");
			const id = feature.getProperty("id");

			if(idsMatch(selectedIdsByFieldId[fieldId], id)) {
				this.selectedFeatures[fieldId] = feature;
			}
		});

		this.revertStyle();
	}

	setHoverStyle(feature) {
		if(!this.layer || typeof this.layer.overrideStyle !== "function") {
			return;
		}

		this.revertStyle();
		this.layer.overrideStyle(feature, {
			fillOpacity: 0.8
		});
	}

	revertStyle() {
		if(!this.layer || typeof this.layer.revertStyle !== "function") {
			return;
		}

		this.layer.revertStyle();

		Object.keys(this.selectedFeatures).forEach((fieldId) => {
			const selectedFeature = this.selectedFeatures[fieldId];

			selectedFeature && this.layer.overrideStyle(selectedFeature, {
				fillOpacity: 0.8
			});
		});
	}

	getFeatureCollection(item, fieldId) {
		if(item.Shape?.GeoJson) {
			let geoJson = null;

			try {
				geoJson = JSON.parse(this._base64.decode(item.Shape.GeoJson));
			}
			catch(err) {
				this.$logger.warn(`Failed to decode GeoJson for related field ${fieldId}, item ${item.Id}`);
				return null;
			}

			const shapeFeatures = geoJson.type === "Feature"
				? [geoJson]
				: (geoJson.features ?? []);

			return {
				type: "FeatureCollection",
				features: shapeFeatures.reduce((features, currentFeature) => {
					const feature = {
						...currentFeature,
						properties: {
							...(currentFeature.properties ?? {}),
							id: item.Id,
							fieldId,
							name: item.Name ?? item.Code,
							feature: null
						}
					};

					feature.properties.feature = feature;
					features.push(feature);
					return features;
				}, [])
			};
		}

		if(item.Geometries && item.Geometries.length > 0) {
			const features = item.Geometries
				.filter((geometry) => geometry?.Type?.Code === "GEOJSON")
				.reduce((features, geometry) => {
					const geoJsonDefinition = geometry?.Definition;

					if(!geoJsonDefinition) {
						return features;
					}

					const newFeatures = toFeatures(geoJsonDefinition, {
						id: item.Id,
						fieldId,
						name: item.Name ?? item.Code
					}, this.$logger);

					newFeatures !== undefined && features.push(...newFeatures);
					return features;
				}, []);

			if(!features.length) {
				return null;
			}

			return {
				type: "FeatureCollection",
				features: features
			};
		}

		return null;
	}

	getRelatedFieldItems(field) {
		const data = field?.Data ?? [];

		return data.reduce((items, item) => {
			items.push(item);

			(item?.Entities ?? []).forEach((entry) => {
				if(entry && entry.Entity) {
					items.push(entry.Entity);
				}
			});

			return items;
		}, []);
	}

	getRelatedFieldGeoJson() {
		const relatedFields = this.lookup?.relatedFields ?? [];

		return relatedFields.reduce((collections, field) => {
			const collection = this.getRelatedFieldItems(field).reduce((featureCollection, item) => {
				const geoJson = this.getFeatureCollection(item, field.Id);

				if(geoJson) {
					geoJson.features.forEach(feature => {
						featureCollection.features.push(feature);
					});
				}

				return featureCollection;
			}, { type: "FeatureCollection", features: [] });

			if(collection.features.length > 0) {
				collections.push(collection);
			}

			return collections;
		}, []);
	}

	getDefaultStyle(param) {
		const feature = param.getProperty("feature");
		const geometryType = feature?.geometry?.type;

		return {
			strokeColor: "#9cbcd8",
			strokeOpacity: 0.8,
			strokeWeight: 1,
			fillColor: "#9cbcd8",
			fillOpacity: 0.0,
			optimized: false,
			icon: geometryType === "Point"
				? {
					url: reefIcon,
					scaledSize: new google.maps.Size(16, 16)
				}
				: ""
		};
	}

	loadElevation(point) {
		this._mapHelpers.elevationLoader.load(point).then(result => {
			this.elevation = {
				...result,
				position: point
			};

			this.updateInfoWindow(this.infoWindow, this.elevation);

			if(this.selected.position)
				this.updateInfoWindow(this.selected.infoWindow, this.elevation);
		});
	}

	updateInfoWindow(infoWindow, elevation) {
		const currentPosition = this.getCoordinates(infoWindow.position);
		if(JSON.stringify(currentPosition) == JSON.stringify(elevation.position)) {
			const content = this.calloutHtml(elevation.position, elevation);

			infoWindow.setContent(content);
		}
	}

	getCoordinates(latLng) {
		return {
			lat: latLng.lat(),
			lng: latLng.lng()
		};
	}

	getEvents() {
		const ctrl = this;
		const throttledUpdateInfoBox = throttle(updateInfoBoxPosition, parseInt(1000.0 / 60.0));

		return {
			// show on hover, but without marker
			mousemove(event) {
				if(ctrl.updatable) {
					event.feature && ctrl.setHoverStyle(event.feature);
					throttledUpdateInfoBox(ctrl, event, false);
				}
			},
			mouseout() {
				ctrl.revertStyle();
				ctrl.marker.setMap(null);
				ctrl.infoWindow.close();
			},
			click(event) {
				if(ctrl.updatable) {
					ctrl.elevation = undefined;
					ctrl.selected.position = ctrl.getCoordinates(event.latLng);
					ctrl.setRelatedFieldSelection(event.feature);
					ctrl.revertStyle();
					updateInfoBoxPosition(ctrl, event);

					ctrl.setStationaryMarker(ctrl);
				}
			}
		};

		function updateInfoBoxPosition(ctrl, event, showFullInfo = true) {
			const point = ctrl.getCoordinates(event.latLng);

			if(showFullInfo) {
				const content = ctrl.calloutHtml(point, ctrl.elevation, showFullInfo);
				ctrl.debouncedLoadElevation(point);
				ctrl.showInfoWindow(content, point);
				ctrl.marker.setPosition(new window.google.maps.LatLng(point));
				ctrl.marker.setMap(ctrl.mapInstance);
			}
			if(!showFullInfo) {
				const content = ctrl.hoverHtml(point);
				ctrl.popup.setPosition(event.latLng);
				ctrl.popup.setText(content);
			}
		}
	}

	setStationaryMarker(ctrl) {
		ctrl.selected.infoWindow.setContent(ctrl.infoWindow.content);
		ctrl.selected.infoWindow.setPosition(new window.google.maps.LatLng(ctrl.selected.position));
		ctrl.selected.infoWindow.open(ctrl.mapInstance);

		ctrl.selected.marker.setPosition(new window.google.maps.LatLng(ctrl.selected.position));
		ctrl.selected.marker.setMap(ctrl.mapInstance);
	}

	showInfoWindow(content, coordinates) {
		this.infoWindow.setContent(content);

		this.infoWindow.setPosition(new window.google.maps.LatLng(coordinates));

		this.infoWindow.open(this.mapInstance);
	}

	initializeMap() {
		const ctrl = this;
		this.mapInstance = new google.maps.Map(document.getElementById("map"), {
			gestureHandling: "greedy",
			streetViewControl: false
		});

		this.mapInstance.setMapTypeId(window.google.maps.MapTypeId.HYBRID);
		window.google.maps.event.trigger(this.mapInstance, "resize");

		CustomPopup.prototype.__proto__ = window.google.maps.OverlayView.prototype;
		this.popup = new CustomPopup(
			document.getElementById("custom-popup")
		);
		this.popup.setMap(ctrl.mapInstance);

		setTimeout(function() {
			google.maps.event.trigger(ctrl.mapInstance, "resize");
			let zoom = ctrl.mapInstance.getZoom() ?? settings.selectedZoom;
			let didFitFeatureBounds = false;

			// setup default map bounds given feature data or US
			if(ctrl.hasFeatureLayer) {
				const bounds = new google.maps.LatLngBounds();
				let hasFeatureCoordinates = false;
				ctrl.layer.forEach(function(feature) {
					const geometry = feature.getGeometry();

					if(geometry && typeof geometry.forEachLatLng === "function") {
						geometry.forEachLatLng(function(latlng) {
							bounds.extend(latlng);
							hasFeatureCoordinates = true;
						});
					}
				});

				if(hasFeatureCoordinates) {
					ctrl.mapInstance.fitBounds(bounds);

					zoom = ctrl.mapInstance.getZoom() ?? settings.selectedZoom;
					ctrl.mapInstance.setZoom(zoom + 0.5);
					didFitFeatureBounds = true;
				}
			} 

			//if there is a position, then zoom to it.  Else, try and use the default position
			if(!didFitFeatureBounds) {
				if(ctrl.selected.position) {
					ctrl.mapInstance.setCenter({
						lat: ctrl.selected.position.lat,
						lng: ctrl.selected.position.lng
					});
					ctrl.mapInstance.setZoom(zoom);
				}
				else if(!!ctrl.resolve.defaultPosition) {
					ctrl.mapInstance.setCenter({
						lat: ctrl.resolve.defaultPosition.center[FIELD_KEYS.latitude],
						lng: ctrl.resolve.defaultPosition.center[FIELD_KEYS.longitude]
					});
					ctrl.mapInstance.setZoom(ctrl.resolve.defaultPosition.zoom ?? settings.selectedZoom);
				}
				else {
					ctrl.mapInstance.fitBounds(defaultUSABounds);
				}
			}


			if(ctrl.lookup.buoys) {
				ctrl.setBuoyLayer();
			}
		}, 600);
	}

	setBuoyLayer() {
		const layer = new google.maps.Data();
		const ctrl = this;

		layer.addGeoJson(this.lookup.buoys);
		layer.setStyle(() => {
			return { icon: reefIcon };
		});
		layer.setMap(this.mapInstance);

		layer.addListener("mouseover", (event) => {
			const name = event.feature.getProperty("NAME") ?? "N/A";

			const point = ctrl.getCoordinates(event.latLng);

			const content = `<h4>${name}</h4>`;
			ctrl.showInfoWindow(content, point);
		});
	}

	setMapLayer() {
		const ctrl = this;
		this.layer = this.mapInstance;
		const lookupGeoJson = ctrl.lookup?.data ?? [];
		const relatedGeoJson = this.getRelatedFieldGeoJson();
		const allGeoJson = lookupGeoJson.concat(relatedGeoJson);
		this.hasFeatureLayer = allGeoJson.length > 0;

		if(allGeoJson.length > 0) {
			this.layer = new google.maps.Data();
			allGeoJson.forEach(g => ctrl.layer.addGeoJson(g));
			this.layer.setStyle((param) => param.getProperty("style") ?? this.getDefaultStyle(param));
			this.layer.setMap(this.mapInstance);
		}

		const events = this.getEvents();
		events.mousemove && this.layer.addListener("mousemove", events.mousemove);
		events.mouseout && this.layer.addListener("mouseout", events.mouseout);
		events.click && this.marker.addListener("click", events.click);
		events.click && this.layer.addListener("click", events.click);
	}

	calloutHtml(point, elevation, showFullInfo = true) {
		const depthDisplay = showFullInfo && elevation?.meters ?
			`<label class='text'>${elevation.isAboveSeaLevel > 0 ? "Elevation" : "Depth"}: &nbsp; </label>
			<label class='dms'>
				${Math.round(elevation.meters)} ${UNITS.meter.abbreviation} ${
				elevation && `(${Math.round(elevation.feet)} ${UNITS.feet.abbreviation}`}.)
			</label>` 
			: "<label class='dms'>Loading...</label>";

		if(this.resolve.lookup.mode === "DECIMAL_DEGREES") {
			const latDMS = this.resolve.lookup.degreeMapper(point.lat);
			const lngDMS = this.resolve.lookup.degreeMapper(point.lng);
			const roundingPoint = this.resolve.lookup.roundingPoint ?? 6; // 6 is the max decimal count stored, so is the default here

			return `
			<div class='latlong'>
				<h5>
					${round(latDMS, roundingPoint)}°
					${round(lngDMS, roundingPoint)}°
				</h5>
				${depthDisplay}
			</div>`;
		}
		else {
			const latDMS = this.resolve.lookup.degreeMapper(point.lat, true);
			const lngDMS = this.resolve.lookup.degreeMapper(point.lng, true);

			return `
			<div class='latlong'>
				<h5>
					${this.formatMappedCoordinate(latDMS, "S", "N")}
					${this.formatMappedCoordinate(lngDMS, "W", "E")}
				</h5>
				${depthDisplay}
			</div>
		`;
		}
	}

	/**
	 *
	 * @point latlng object
	 * @returns html for the custom hover popup
	 */

	hoverHtml(point) {
		const roundingPoint = this.resolve.lookup.roundingPoint ?? 6;

		if(this.resolve.lookup.mode === "DECIMAL_DEGREES") {
			const latDMS = this.resolve.lookup.degreeMapper(point.lat);
			const lngDMS = this.resolve.lookup.degreeMapper(point.lng);

			return `
				<div>
					<h5 class="monospace">
						${round(latDMS, roundingPoint)}°
						${round(lngDMS, roundingPoint)}°
					</h5>
				</div>
		`;

		}
		else {
			const latDMS = this.resolve.lookup.degreeMapper(point.lat, true);
			const lngDMS = this.resolve.lookup.degreeMapper(point.lng, true);

			return `
				<div>
					<h5 class="monospace">
						${this.formatMappedCoordinate(latDMS, "S", "N", true)}
						${this.formatMappedCoordinate(lngDMS, "W", "E", true)}
					</h5>
				</div>
			`;
		}

	}
}

export default angular
	.module("vesl.components.field-helpers.location-modal", [])
	.component("locationModal", {
		controller: Controller,
		template,
		bindings: {
			close: "&",
			dismiss: "&",
			modalInstance: "<",
			resolve: "<"
		}
	}).name;
